import { tools, type GroupedUsersList } from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';

export const useUserSelector = (
  list: GroupedUsersList[],
  checkUserKey = true,
  maxSelectedCount?: number
) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<any[]>([]);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean | 'indeterminate'>(false);

  const listFlat = useMemo(() => {
    return list?.flatMap((item) => item.items);
  }, [list]);

  const canSelectList = useMemo(() => {
    return listFlat.filter((user) => {
      if (user.disabled) return false;
      return checkUserKey ? user.have_group_key && user.have_user_key : true;
    });
  }, [listFlat, checkUserKey]);

  const toggleSelectUser = useCallback(
    (item: any) => {
      setSelected((prev) => {
        const isSelected = prev.some((user) => user.user_id === item.user_id);
        const filterSelectedList = prev.filter((user) => user.user_id !== item.user_id);
        const limitMaxSelectedCount = prev.length >= (maxSelectedCount ?? Infinity);
        if (!isSelected && limitMaxSelectedCount) {
          tools.toaster.error({
            title: t('tips.maxSelectedUsersCount', { count: maxSelectedCount }),
          });
          return prev;
        }
        return isSelected ? filterSelectedList : [...prev, item];
      });
    },
    [maxSelectedCount, t]
  );

  const onSelectAllChange = useCallback(
    (_checked: boolean) => {
      const checked =
        selected.length > 0 &&
        canSelectList.every((user) => selected.some((u) => u.user_id === user.user_id));
      setIsSelectedAll(checked);
      let items: any[] = [];
      if (checked) {
        items = selected.filter((user) => !canSelectList.some((u) => u.user_id === user.user_id));
      } else {
        const deduplicateList = [...canSelectList, ...selected].filter(
          (user, index, self) => index === self.findIndex((t) => t.user_id === user.user_id)
        );
        items = deduplicateList;
      }
      setSelected(items);
    },
    [selected, canSelectList]
  );

  const onRemove = useCallback(
    (item: any) => {
      setSelected((prev) => prev.filter((user) => user.user_id !== item.user_id));
    },
    [setSelected]
  );

  const resetDialogState = useCallback(() => {
    setSelected([]);
    setIsSelectedAll(false);
  }, [setSelected, setIsSelectedAll]);

  const listFlatRef = useRef(listFlat);
  listFlatRef.current = listFlat;

  useEffect(() => {
    const canSelectList = listFlatRef.current;
    const isAllChecked =
      selected.length > 0 &&
      canSelectList.every((user) => selected.some((u) => u.user_id === user.user_id));
    const isIndeterminate = selected.length > 0 && !isAllChecked;

    setIsSelectedAll(isAllChecked ? true : isIndeterminate ? 'indeterminate' : false);
  }, [list, selected]);

  return {
    listFlat,
    selected,
    canSelectList,
    setSelected,
    isSelectedAll,
    setIsSelectedAll,
    toggleSelectUser,
    onSelectAllChange,
    onRemove,
    resetDialogState,
  };
};
